import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import { ChangeRoleButton } from "@ext/enterprise/components/admin/settings/members/components/ChangeRoleButton";
import { PickerDialog } from "@ext/enterprise/components/admin/settings/members/dialogs/PickerDialog";
import { emailKey, type UserMember } from "@ext/enterprise/components/admin/settings/members/model/Member";
import { AddUserSheet } from "@ext/enterprise/components/admin/settings/users/dialog/AddUserSheet";
import {
	type UseUserAccessPickerDialogArgs,
	useUserAccessPickerDialog,
} from "@ext/enterprise/components/admin/settings/users/hooks/useUserAccessPickerDialog";
import { Button } from "@ext/enterprise/components/admin/ui-kit/Button";
import { GesCloudUserAccessPickerDialogContent } from "@ext/enterprise-cloud/components/userAccessPicker/GesCloudUserAccessPickerDialogContent";
import type { UserAccessPickerDialogContentProps } from "@ext/enterpriseCommon/components/userAccessPicker/types/userAccessPickerDialogContentTypes";
import { UserAccessPickerDialogTable } from "@ext/enterpriseCommon/components/userAccessPicker/UserAccessPickerDialogTable";
import {
	type UseUserAccessPickerDialogContentArgs,
	useUserAccessPickerDialogContent,
} from "@ext/enterpriseCommon/hooks/useUserAccessPickerDialogContent";
import t from "@ext/localization/locale/translate";
import { useMemo, useState } from "react";

type UserAccessPickerDialogProps = UseUserAccessPickerDialogArgs &
	Pick<UseUserAccessPickerDialogContentArgs, "aggregate" | "preselected" | "repoId"> & {
		open: boolean;
		onClose: () => void;
	};

export const UserAccessPickerDialog = (props: UserAccessPickerDialogProps) => {
	const { rowsMap, setRowsMap, selection, setSelection, selectedIds, picked } = useUserAccessPickerDialog(props);
	const isCloud = !!PageDataContextService.value?.conf?.enterpriseCloud?.url;
	const Content = isCloud ? GesCloudUserAccessPickerDialogContent : EnterpriseUserAccessPickerDialogContent;

	return (
		<PickerDialog
			count={selectedIds.length}
			onClose={props.onClose}
			onPicked={picked}
			open={props.open}
			title={t("enterprise.admin.resources.adding-user-accesses")}
		>
			<Content
				aggregate={props.aggregate}
				preselected={props.preselected}
				repoId={props.repoId}
				rowsMap={rowsMap}
				selectedIds={selectedIds}
				selection={selection}
				setRowsMap={setRowsMap}
				setSelection={setSelection}
			/>
		</PickerDialog>
	);
};

const EnterpriseUserAccessPickerDialogContent = (props: UserAccessPickerDialogContentProps) => {
	const [addNewOpen, setAddNewOpen] = useState(false);
	const [createdUsers, setCreatedUsers] = useState<UserMember[]>([]);
	const { data, headerLeftControls, actions } = useUserAccessPickerDialogContent({
		...props,
		extraUsers: createdUsers,
	});

	const existingIds = useMemo(
		() => new Set(props.aggregate.users.map((x) => emailKey(x.value))),
		[props.aggregate.users],
	);

	const toolbarActions = (
		<>
			<ChangeRoleButton
				count={props.selectedIds.length}
				onChange={actions.bulkRoleChange}
				rules={data.roleRules}
			/>
			{!data.ssoEnabled && (
				<>
					<Button
						className="pl-2.5 pr-3"
						onClick={() => setAddNewOpen(true)}
						startIcon="plus"
						variant="outline"
					>
						{t("enterprise.admin.users.add")}
					</Button>
					<AddUserSheet
						existingKeys={existingIds}
						onCreate={(id: string) => {
							actions.createNew(id);
							setCreatedUsers((prev) => [
								...prev,
								{
									value: id,
									isEditor: false,
									isWorkspaceOwner: false,
								},
							]);
						}}
						onOpenChange={setAddNewOpen}
						open={addNewOpen}
					/>
				</>
			)}
		</>
	);

	return (
		<UserAccessPickerDialogTable
			data={data}
			headerLeftControls={headerLeftControls}
			selection={props.selection}
			setSelection={props.setSelection}
			toolbarActions={toolbarActions}
		/>
	);
};
